import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, Badge, Spinner, Alert, Table, Accordion,
  Dropdown, ButtonGroup, Form, Modal, InputGroup, ListGroup,
} from 'react-bootstrap';
import { API_URL, getDeviceId } from '../api';

// --- Types ---

interface MatchCandidate {
  product_id: number;
  name: string;
  similarity: number;
  source: string;
}

interface ReceiptLine {
  id: number;
  line_number: number;
  raw_text: string;
  parsed_name: string | null;
  parsed_price: number | null;
  parsed_quantity: number | null;
  is_promotion: boolean;
  product_id: number | null;
  product_name: string | null;
  concept_id: number | null;
  concept_name: string | null;
  match_confidence: number | null;
  match_method: string | null;
  match_candidates: MatchCandidate[] | null;
  is_user_verified: boolean;
  is_dismissed: boolean;
}

interface StoreMatchInfo {
  store_id: number;
  store_name: string;
  chain_name: string;
  match_method: string | null;
  confidence: number | null;
}

interface ReceiptDetail {
  id: number;
  device_id: string;
  store_id: number | null;
  image_key: string;
  purchase_date: string | null;
  total_amount: number | null;
  currency: string;
  status: string;
  uploaded_at: string | null;
  store_info: StoreMatchInfo | null;
  lines: ReceiptLine[];
}

interface ConceptSuggestion {
  type: 'concept' | 'product';
  id: number;
  name: string;
  icon: string | null;
}

interface CreateProductModalState {
  receiptId: number;
  lineId: number;
  name: string;
  conceptId: number | null;
  conceptName: string | null;
}

interface StoreSearchResult {
  store_id: number;
  store_name: string;
  chain_name: string;
  street: string | null;
  postal_code: string | null;
  city: string | null;
}

interface ReceiptSummary {
  id: number;
  device_id: string;
  store_id: number | null;
  image_key: string;
  purchase_date: string | null;
  total_amount: number | null;
  currency: string;
  status: string;
  uploaded_at: string | null;
  store_name: string | null;
  chain_name: string | null;
  line_count: number;
  matched_count: number;
}

// --- Helpers ---

const STATUS_MAP: Record<string, { bg: string; label: string }> = {
  uploaded:   { bg: 'secondary', label: 'Hochgeladen' },
  processing: { bg: 'info',      label: 'Verarbeitung...' },
  extracted:  { bg: 'primary',   label: 'Erkannt' },
  confirmed:  { bg: 'success',   label: 'Bestätigt' },
  rejected:   { bg: 'danger',    label: 'Abgelehnt' },
  failed:     { bg: 'warning',   label: 'Fehlgeschlagen' },
};

function statusBadge(status: string) {
  const s = STATUS_MAP[status] || { bg: 'secondary', label: status };
  return <Badge bg={s.bg}>{s.label}</Badge>;
}

function confidenceBadge(confidence: number | null) {
  if (confidence === null) return <Badge bg="secondary">?</Badge>;
  const pct = Math.round(confidence * 100);
  const bg = pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'danger';
  return <Badge bg={bg}>{pct}%</Badge>;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('de-AT');
  } catch {
    return dateStr;
  }
}

function formatPrice(price: number | null): string {
  if (price === null) return '—';
  return `${price.toFixed(2)} €`;
}

// --- Component ---

const Receipts: React.FC = () => {
  const [receipts, setReceipts] = useState<ReceiptSummary[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [receiptDetails, setReceiptDetails] = useState<Record<number, ReceiptDetail>>({});
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [ocrEngine, setOcrEngine] = useState<string>('');
  const [lineActionLoading, setLineActionLoading] = useState<number | null>(null);

  // Manual store assignment
  const [storeQuery, setStoreQuery] = useState('');
  const [storeResults, setStoreResults] = useState<StoreSearchResult[]>([]);
  const [storeSearching, setStoreSearching] = useState(false);

  // Create-product modal
  const [createModal, setCreateModal] = useState<CreateProductModalState | null>(null);
  const [conceptQuery, setConceptQuery] = useState('');
  const [conceptSuggestions, setConceptSuggestions] = useState<ConceptSuggestion[]>([]);
  const conceptDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const deviceId = getDeviceId();

  // --- API ---

  const fetchReceipts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ device_id: deviceId });
      const res = await fetch(`${API_URL}/receipts/?${params}`);
      if (!res.ok) throw new Error('Fehler beim Laden der Bons');
      const data: ReceiptSummary[] = await res.json();
      setReceipts(data);
      return data;
    } catch (e: any) {
      setError(e.message);
      return [];
    }
  }, [deviceId]);

  const fetchDetail = useCallback(async (receiptId: number) => {
    setDetailLoading(receiptId);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}`);
      if (!res.ok) throw new Error('Bon nicht gefunden');
      const data: ReceiptDetail = await res.json();
      setReceiptDetails(prev => ({ ...prev, [receiptId]: data }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDetailLoading(null);
    }
  }, []);

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('device_id', deviceId);
      formData.append('file', file);

      const engineParam = ocrEngine ? `?engine=${ocrEngine}` : '';
      const res = await fetch(`${API_URL}/receipts/upload${engineParam}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({ detail: 'Upload fehlgeschlagen' }));
        throw new Error(detail.detail || 'Upload fehlgeschlagen');
      }
      const created: ReceiptDetail = await res.json();
      await fetchReceipts();
      setReceiptDetails(prev => ({ ...prev, [created.id]: created }));
      setExpandedId(String(created.id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const confirmReceipt = async (receiptId: number) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/confirm`, { method: 'PUT' });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({ detail: 'Bestätigung fehlgeschlagen' }));
        throw new Error(detail.detail || 'Bestätigung fehlgeschlagen');
      }
      const updated: ReceiptDetail = await res.json();
      setReceiptDetails(prev => ({ ...prev, [receiptId]: updated }));
      await fetchReceipts();
      setSuccessMsg('Bon bestätigt! Danke für deinen Beitrag.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const rejectReceipt = async (receiptId: number) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/reject`, { method: 'PUT' });
      if (!res.ok) throw new Error('Ablehnung fehlgeschlagen');
      const updated: ReceiptDetail = await res.json();
      setReceiptDetails(prev => ({ ...prev, [receiptId]: updated }));
      await fetchReceipts();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteReceipt = async (receiptId: number) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Löschen fehlgeschlagen');
      setExpandedId(null);
      setReceiptDetails(prev => {
        const copy = { ...prev };
        delete copy[receiptId];
        return copy;
      });
      await fetchReceipts();
      setSuccessMsg('Bon gelöscht.');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const searchStores = async (q: string) => {
    setStoreQuery(q);
    if (q.trim().length < 2) {
      setStoreResults([]);
      return;
    }
    setStoreSearching(true);
    try {
      const res = await fetch(`${API_URL}/stores/search?q=${encodeURIComponent(q)}&limit=20`);
      if (!res.ok) throw new Error('Store-Suche fehlgeschlagen');
      setStoreResults(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setStoreSearching(false);
    }
  };

  const assignStore = async (receiptId: number, storeId: number) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/store`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: storeId }),
      });
      if (!res.ok) throw new Error('Store-Zuweisung fehlgeschlagen');
      const updated: ReceiptDetail = await res.json();
      setReceiptDetails(prev => ({ ...prev, [receiptId]: updated }));
      await fetchReceipts();
      setStoreQuery('');
      setStoreResults([]);
      setSuccessMsg('Store zugewiesen. Du kannst den Bon jetzt bestätigen.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // --- Line actions ---

  const matchLine = async (receiptId: number, lineId: number, productId: number) => {
    setLineActionLoading(lineId);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/lines/${lineId}/match`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId }),
      });
      if (!res.ok) throw new Error('Zuordnung fehlgeschlagen');
      await fetchDetail(receiptId);
      await fetchReceipts();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLineActionLoading(null);
    }
  };

  const dismissLine = async (receiptId: number, lineId: number) => {
    setLineActionLoading(lineId);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/lines/${lineId}/dismiss`, {
        method: 'PUT',
      });
      if (!res.ok) throw new Error('Aktion fehlgeschlagen');
      await fetchDetail(receiptId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLineActionLoading(null);
    }
  };

  const createProductFromLine = async (receiptId: number, lineId: number, name: string, conceptId?: number) => {
    setLineActionLoading(lineId);
    try {
      const res = await fetch(`${API_URL}/receipts/${receiptId}/lines/${lineId}/create-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, concept_id: conceptId || null }),
      });
      if (!res.ok) throw new Error('Produkt-Erstellung fehlgeschlagen');
      await fetchDetail(receiptId);
      await fetchReceipts();
      setSuccessMsg(`Produkt "${name}" angelegt.`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLineActionLoading(null);
    }
  };

  const openCreateModal = (receiptId: number, line: ReceiptLine) => {
    setCreateModal({
      receiptId,
      lineId: line.id,
      name: line.parsed_name || line.raw_text || '',
      conceptId: line.concept_id,
      conceptName: line.concept_name,
    });
    setConceptQuery(line.concept_name || '');
    setConceptSuggestions([]);
  };

  const closeCreateModal = () => {
    setCreateModal(null);
    setConceptQuery('');
    setConceptSuggestions([]);
    if (conceptDebounceRef.current) clearTimeout(conceptDebounceRef.current);
  };

  const fetchConceptSuggestions = (query: string) => {
    if (conceptDebounceRef.current) clearTimeout(conceptDebounceRef.current);
    if (query.trim().length < 2) {
      setConceptSuggestions([]);
      return;
    }
    conceptDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: '8' });
        const res = await fetch(`${API_URL}/shopping_lists/autocomplete?${params}`);
        if (!res.ok) return;
        const data: ConceptSuggestion[] = await res.json();
        setConceptSuggestions(data.filter(s => s.type === 'concept'));
      } catch {
        // ignore
      }
    }, 300);
  };

  const submitCreateModal = async () => {
    if (!createModal) return;
    const name = createModal.name.trim();
    if (!name) return;
    await createProductFromLine(
      createModal.receiptId,
      createModal.lineId,
      name,
      createModal.conceptId || undefined,
    );
    closeCreateModal();
  };

  // --- Effects ---

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchReceipts();
      setLoading(false);
    })();
  }, [fetchReceipts]);

  // Load detail when accordion item is expanded
  const handleAccordionSelect = (eventKey: string | null) => {
    setExpandedId(eventKey);
    if (eventKey) {
      const id = Number(eventKey);
      if (!receiptDetails[id]) {
        fetchDetail(id);
      }
    }
  };

  // --- Render helpers ---

  const renderLines = (detail: ReceiptDetail) => {
    const canEdit = !['confirmed', 'rejected'].includes(detail.status);

    return (
      <Table size="sm" responsive className="mb-0 small">
        <thead>
          <tr>
            <th>Produkt (Bon)</th>
            <th style={{ width: '4.5rem' }} className="text-end">Preis</th>
            <th>Zuordnung</th>
            <th style={{ width: '4rem' }} className="text-center"></th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map(line => {
            const isLoading = lineActionLoading === line.id;

            return (
              <tr key={line.id} className={line.is_dismissed ? 'text-decoration-line-through text-muted' : ''}>
                <td>
                  {line.parsed_name || line.raw_text}
                  {line.is_promotion && (
                    <Badge bg="warning" text="dark" className="ms-1">Aktion</Badge>
                  )}
                  {(line.parsed_quantity ?? 1) > 1 && (
                    <Badge bg="light" text="dark" className="ms-1">{line.parsed_quantity}x</Badge>
                  )}
                </td>
                <td className="text-end text-nowrap">{formatPrice(line.parsed_price)}</td>
                <td>
                  {line.is_dismissed ? (
                    <span className="text-muted fst-italic">Ignoriert</span>
                  ) : line.product_name ? (
                    <span className={line.is_user_verified ? 'text-success fw-bold' : 'text-success'}>
                      {line.product_name}
                      {line.is_user_verified && <span className="ms-1">&#10003;</span>}
                    </span>
                  ) : line.match_candidates && line.match_candidates.length > 0 ? (
                    <Dropdown as={ButtonGroup} size="sm">
                      <Dropdown.Toggle variant="outline-warning" size="sm" disabled={isLoading || !canEdit}>
                        {isLoading ? <Spinner animation="border" size="sm" /> : `${line.match_candidates.length} Vorschläge`}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {line.match_candidates.map((c: MatchCandidate) => (
                          <Dropdown.Item
                            key={c.product_id}
                            onClick={() => matchLine(detail.id, line.id, c.product_id)}
                          >
                            {c.name} <Badge bg="secondary" className="ms-1">{Math.round(c.similarity * 100)}%</Badge>
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => openCreateModal(detail.id, line)}>
                          <span className="text-success">+ Neues Produkt anlegen…</span>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : line.concept_name ? (
                    <span className="text-info fst-italic">~ {line.concept_name}</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-center text-nowrap">
                  {!line.is_dismissed && !line.product_id && canEdit && (
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-success"
                        size="sm"
                        title="Als neues Produkt anlegen"
                        disabled={isLoading}
                        onClick={() => openCreateModal(detail.id, line)}
                      >+</Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Ignorieren (Pfand, Gutschein etc.)"
                        disabled={isLoading}
                        onClick={() => dismissLine(detail.id, line.id)}
                      >&times;</Button>
                    </ButtonGroup>
                  )}
                  {line.product_id && !line.is_dismissed && confidenceBadge(line.match_confidence)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // --- Main render ---

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" /> <span className="ms-2">Laden...</span>
      </div>
    );
  }

  const confirmedCount = receipts.filter(r => r.status === 'confirmed').length;

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Upload + Stats row */}
      <div className="d-flex gap-2 mb-3 align-items-stretch">
        <Card className="flex-grow-1">
          <Card.Body className="text-center py-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadFile(file);
                e.target.value = '';
              }}
            />
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-100"
            >
              {uploading ? (
                <><Spinner animation="border" size="sm" className="me-2" />OCR läuft...</>
              ) : (
                'Kassenbon hochladen'
              )}
            </Button>
            <Form.Select
              size="sm"
              className="mt-2"
              value={ocrEngine}
              onChange={(e) => setOcrEngine(e.target.value)}
            >
              <option value="">OCR: Standard</option>
              <option value="tesseract">Tesseract (lokal)</option>
              <option value="mistral">Mistral AI (Cloud)</option>
            </Form.Select>
          </Card.Body>
        </Card>
        {confirmedCount > 0 && (
          <Card className="border-success" style={{ minWidth: '6rem' }}>
            <Card.Body className="text-center py-2">
              <div className="fs-4 fw-bold text-success">{confirmedCount}</div>
              <small className="text-muted">bestätigt</small>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Receipt list as accordion */}
      {receipts.length === 0 ? (
        <Alert variant="light" className="text-muted text-center">
          Noch keine Bons hochgeladen.
        </Alert>
      ) : (
        <Accordion activeKey={expandedId} onSelect={(k) => handleAccordionSelect(k as string | null)}>
          {receipts.map(r => {
            const detail = receiptDetails[r.id];
            const isExpanded = expandedId === String(r.id);
            const canAct = detail && !['confirmed', 'rejected'].includes(detail.status);

            return (
              <Accordion.Item eventKey={String(r.id)} key={r.id}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-2">
                    <div>
                      <strong>{r.chain_name || r.store_name || 'Unbekannt'}</strong>
                      <span className="text-muted ms-2">
                        {formatDate(r.purchase_date)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold">{formatPrice(r.total_amount)}</span>
                      {statusBadge(r.status)}
                      <small className="text-muted">{r.matched_count}/{r.line_count}</small>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  {detailLoading === r.id ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" /> <span className="ms-2">Laden...</span>
                    </div>
                  ) : detail ? (
                    <>
                      {/* Store info */}
                      {detail.store_info && (
                        <div className="px-3 pt-2 pb-1 bg-light small text-muted">
                          {detail.store_info.chain_name} — {detail.store_info.store_name}
                          {detail.store_info.confidence !== null && (
                            <span className="ms-2">Store-Match: {confidenceBadge(detail.store_info.confidence)}</span>
                          )}
                        </div>
                      )}

                      {/* Lines */}
                      {detail.lines.length > 0 && renderLines(detail)}

                      {/* Manuelle Store-Zuweisung, wenn kein Auto-Match */}
                      {canAct && !detail.store_id && (
                        <div className="p-2 border-top">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <small className="text-muted">Kein Store-Match — Markt manuell zuweisen:</small>
                          </div>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Suche nach Kette, Ort, PLZ … (z.B. Penny 2700)"
                            value={storeQuery}
                            onChange={(e) => searchStores(e.target.value)}
                          />
                          {storeSearching && (
                            <div className="mt-1"><Spinner animation="border" size="sm" /></div>
                          )}
                          {storeResults.length > 0 && (
                            <ListGroup className="mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                              {storeResults.map((s) => (
                                <ListGroup.Item
                                  key={s.store_id}
                                  action
                                  disabled={actionLoading}
                                  onClick={() => assignStore(detail.id, s.store_id)}
                                  className="py-1"
                                >
                                  <strong>{s.chain_name}</strong> — {s.store_name}
                                  <br />
                                  <small className="text-muted">
                                    {[s.street, s.postal_code, s.city].filter(Boolean).join(', ')}
                                  </small>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="d-flex gap-2 p-2 border-top">
                        {canAct && (
                          <>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => confirmReceipt(detail.id)}
                              disabled={actionLoading || !detail.store_id}
                            >
                              {actionLoading ? <Spinner animation="border" size="sm" /> : 'Bestätigen'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => rejectReceipt(detail.id)}
                              disabled={actionLoading}
                            >
                              Ablehnen
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="ms-auto"
                          onClick={() => deleteReceipt(detail.id)}
                          disabled={actionLoading}
                          title="Bon löschen"
                        >
                          Löschen
                        </Button>
                      </div>
                    </>
                  ) : null}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      <Modal show={createModal !== null} onHide={closeCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Neues Produkt anlegen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              submitCreateModal();
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Produktname</Form.Label>
              <Form.Control
                type="text"
                value={createModal?.name || ''}
                onChange={(e) =>
                  setCreateModal(m => m ? { ...m, name: e.target.value } : m)
                }
                autoFocus
              />
              <Form.Text className="text-muted">
                Wird so wie hier eingegeben angelegt und als Alias für künftige Bons gelernt.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Konzept (Kategorie)</Form.Label>
              {createModal?.conceptId ? (
                <InputGroup>
                  <InputGroup.Text className="bg-info-subtle">
                    ~ {createModal.conceptName || `#${createModal.conceptId}`}
                  </InputGroup.Text>
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      setCreateModal(m => m ? { ...m, conceptId: null, conceptName: null } : m)
                    }
                  >
                    Ändern
                  </Button>
                </InputGroup>
              ) : (
                <>
                  <Form.Control
                    type="text"
                    placeholder="z.B. Tafelessig, Mehl, Mineralwasser…"
                    value={conceptQuery}
                    onChange={(e) => {
                      setConceptQuery(e.target.value);
                      fetchConceptSuggestions(e.target.value);
                    }}
                  />
                  {conceptSuggestions.length > 0 && (
                    <ListGroup className="mt-1" style={{ maxHeight: '12rem', overflowY: 'auto' }}>
                      {conceptSuggestions.map(s => (
                        <ListGroup.Item
                          key={s.id}
                          action
                          onClick={() =>
                            setCreateModal(m =>
                              m ? { ...m, conceptId: s.id, conceptName: s.name } : m
                            )
                          }
                        >
                          {s.icon && <span className="me-2">{s.icon}</span>}
                          {s.name}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  <Form.Text className="text-muted">
                    Optional, aber empfohlen — verbessert künftiges Matching deutlich.
                  </Form.Text>
                </>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCreateModal}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!createModal?.name.trim() || lineActionLoading === createModal?.lineId}
            onClick={submitCreateModal}
          >
            {lineActionLoading === createModal?.lineId
              ? <Spinner animation="border" size="sm" />
              : 'Anlegen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Receipts;
